import { useAsync2 } from "nate-react-api-helpers";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/API";
import { Team } from "../api/Teams";
import { pagePath } from "../pages/task/taskUrl";
import { useCurrentUser } from "./CurrentUser";

const lastWorkspaceKey = "workspace.last";

// allWorkspace is the url segment for every team the user is on
export const allWorkspace = "all";

// lastWorkspace is the workspace the user was in last, which is where a link without
// one opens
export function lastWorkspace() {
    try {
        return localStorage.getItem(lastWorkspaceKey) || allWorkspace;
    } catch (e) {
        return allWorkspace;
    }
}

function rememberWorkspace(value: string) {
    try {
        localStorage.setItem(lastWorkspaceKey, value);
    } catch (e) {
        // the next visit opens in "All"
    }
}

type Teams = {
    teams: Team[];
    loading: boolean;
    error: string | null;
    reload: () => void;
}

const TeamsContext = createContext<Teams>({ teams: [], loading: false, error: null, reload: () => {} });

// isPlainAdmin is the built-in admin account while it isn't viewing as another user.
// It's on no team, so it only has the admin page.
export function useIsPlainAdmin() {
    const me = useCurrentUser();
    return !!me && me.role === "admin" && !me.masqueradeBy;
}

// WorkspaceProvider holds the teams the user is on, which every page reads to name
// and switch its workspace
export function WorkspaceProvider(props: PropsWithChildren<{}>) {
    const me = useCurrentUser();
    const plainAdmin = useIsPlainAdmin();
    const userId = me?.id || 0;

    const list = useAsync2(async (input: { user: number; skip: boolean }) => {
        if (!input.user || input.skip) return [] as Team[];
        return api.teams.list();
    }, { user: userId, skip: plainAdmin }, [userId, plainAdmin]);

    return (
        <TeamsContext.Provider value={{
            teams: list.result || [],
            loading: list.loading,
            error: list.error,
            reload: list.reload,
        }}>
            {props.children}
        </TeamsContext.Provider>
    );
}

export type Workspace = {
    // key is the url segment: a team id or "all"
    key: string;

    // team is 0 for "All"
    team: number;

    teams: Team[];
    current: Team | null;
    personal: Team | null;

    // name is what the switcher shows
    name: string;

    // newTeam is the team a task or project made here goes to: the workspace's team,
    // or the personal team in "All"
    newTeam: number;

    // path is a page of this workspace, e.g. path("/tasks/day")
    path: (page: string) => string;

    loading: boolean;
    error: string | null;
    reload: () => void;
}

// useWorkspace reads the workspace from the url. Outside a /w/ route it's the last
// workspace the user was in.
export function useWorkspace(): Workspace {
    const params = useParams();
    const teams = useContext(TeamsContext);

    const key = params.workspace || lastWorkspace();
    const team = key === allWorkspace ? 0 : (parseInt(key, 10) || 0);
    const current = teams.teams.find(row => row.id === team) || null;
    const personal = teams.teams.find(row => row.personal) || null;

    return {
        key: team ? String(team) : allWorkspace,
        team: team,
        teams: teams.teams,
        current: current,
        personal: personal,
        name: teamName(current, team),
        newTeam: team || personal?.id || 0,
        path: (page: string) => "/w/" + (team ? team : allWorkspace) + page,
        loading: teams.loading,
        error: teams.error,
        reload: teams.reload,
    };
}

// isPersonalTeam is true for the user's own team, where nobody else can be assigned
export function isPersonalTeam(teams: Team[], team: number) {
    return !!teams.find(row => row.id === team)?.personal;
}

// teamName is what a team is called in the switcher and on a chip
export function teamName(value: Team | null, team: number) {
    if (!team) return "All";
    if (!value) return "…";
    return value.personal ? "Personal" : value.name;
}

// RememberWorkspace keeps the workspace in the url as the one to open next time
export function RememberWorkspace() {
    const params = useParams();
    const key = params.workspace || "";

    useEffect(() => {
        if (key) rememberWorkspace(key);
    }, [key]);

    return null;
}

// switchPath is the same page under another workspace. What's open in a panel may be
// on another team, so it's closed.
export function switchPath(location: { pathname: string; search: string }, to: string) {
    let page = pagePath(location.pathname.replace(/^\/w\/[^/]+/, ""));

    // the "All" workspace has a list of teams where a team has its settings
    if (to === allWorkspace && page === "/team") page = "/teams";
    if (to !== allWorkspace && page === "/teams") page = "/team";

    // a project page is of one project, which isn't in the other workspace
    if (page.startsWith("/projects/")) page = "/projects";

    if (!page.startsWith("/")) page = "/tasks";

    const params = new URLSearchParams(location.search);
    for (const key of ["task", "project", "goal", "fromTask", "panel"]) params.delete(key);

    const search = params.toString();
    return "/w/" + to + page + (search ? "?" + search : "");
}
